/**
 * Delete Organization Danger Zone Component
 */

import type { JSX } from 'react';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { addToast, Button, useDisclosure } from '@heroui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { usePermission } from '~/hooks/usePermission';
import { useTenant } from '~/hooks/useTenant';
import { deleteOrganization } from '~/services/api/organizations';

import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import { Input } from '~/components/Field';
import { LoadingSpinner } from '~/components/LoadingSpinner';

export function DangerZone(): JSX.Element {
  const { canDeleteOrg } = usePermission();
  const { currentOrg } = useTenant();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [confirmSlug, setConfirmSlug] = useState('');

  const slugMatches = confirmSlug === currentOrg?.slug;

  const { isPending, mutate } = useMutation({
    mutationKey: ['deleteOrganization', currentOrg?.id],
    mutationFn: async () => {
      if (!currentOrg) throw new Error('No organization selected');
      return deleteOrganization(currentOrg.id, currentOrg.slug);
    },
    onSuccess: () => {
      // R5.3-10: Clear context and navigate BEFORE showing toast
      // Invalidate all organization queries to clear TenantContext
      void queryClient.invalidateQueries({ queryKey: ['organization'] });

      addToast({
        description: 'Organization deleted successfully',
        color: 'success',
      });

      // Navigate to onboarding (TenantContext will redirect if no org)
      void navigate('/onboarding', { replace: true });
    },
    onError: (error: Error) => {
      addToast({
        description: error.message || 'Failed to delete organization',
        color: 'danger',
      });
    },
  });

  const handleDelete = (): void => {
    mutate();
  };

  const handleModalClose = (): void => {
    setConfirmSlug('');
    onClose();
  };

  // Defense in depth: redirect if user doesn't have permission
  if (!canDeleteOrg) {
    return <Navigate to="/org/settings" replace />;
  }

  if (!currentOrg) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <h3 className="text-xl font-semibold mb-2">Delete Organization</h3>
      <p>
        Permanently delete <strong>{currentOrg.name}</strong> and all its data. This action cannot
        be undone.
      </p>
      <ul className="text-sm my-4 list-disc list-inside space-y-1">
        <li>All projects and tasks will be deleted</li>
        <li>All team memberships will be removed</li>
        <li>All pending invitations will be cancelled</li>
        <li>This action is permanent and irreversible</li>
      </ul>
      <Button color="danger" onPress={onOpen}>
        Delete Organization
      </Button>

      <ConfirmationDialog
        content={
          <div className="flex flex-col gap-4">
            <p>
              This will permanently delete <strong>{currentOrg.name}</strong> and all associated
              data. This action cannot be undone.
            </p>
            <p className="text-sm">
              To confirm, type the organization slug:{' '}
              <span className="bg-surface-elevated px-1.5 py-0.5 rounded font-mono text-sm">
                {currentOrg.slug}
              </span>
            </p>
            <Input
              placeholder={currentOrg.slug}
              value={confirmSlug}
              onChange={e => setConfirmSlug(e.target.value)}
              autoComplete="off"
            />
          </div>
        }
        isDisabled={!slugMatches}
        isOpen={isOpen}
        isPending={isPending}
        onClose={handleModalClose}
        onConfirm={handleDelete}
        title="Delete Organization"
      />
    </div>
  );
}
